import api from './api'
import type {
  LearnerProfile,
  LearnerCoachProfile,
  LearnerSkillProgress,
  LearningActivity,
  LearningHome,
  LearningStreak,
  SkillRecommendation,
  CoachingSession,
  LearningEvidence,
  GuardianLearner,
  GuardianProgressSummaryResponse,
  GuardianShare,
} from '../types/learning'
import type {
  LearningTemplate,
  TemplateAssignment,
  LearnerAttempt,
  AttemptSubmission,
  ContentFeedbackSubmission,
} from '../types/learningContent'

export interface OnboardingPayload {
  learningLanguage?: string
  goals?: string[]
  interests?: string[]
  displayName?: string | null
}

export interface FrenchActionPayload {
  learnerLevel?: string
  languagePreference?: 'fr' | 'en'
  passage?: string | null
  generatedPassageRequest?: string | null
  assignmentGoal?: string | null
  studentParagraph?: string | null
  theme?: string | null
  learnerActivity?: string | null
  conversationSummary?: string | null
  recordActivity?: boolean
}

export interface FrenchActionResult {
  success: boolean
  setupRequired?: boolean
  llmConfigured?: boolean
  result?: Record<string, any> | string
  lessonActivity?: Record<string, any>
  fallback?: boolean
  reason?: 'ai_timeout' | 'ai_error' | 'ai_output_too_large' | 'malformed_ai_output' | string
  message?: string
  provider?: string | null
  model?: string | null
}

// Platform Learning API client. Routes are authentication-gated only, so these
// work for org-less learners as well as workforce users.
export const learningService = {
  getMe: () => api.get<{ success: boolean; profile: LearnerProfile; coach: LearnerCoachProfile }>('/api/learning/me'),
  getHome: () => api.get<{ success: boolean; home: LearningHome }>('/api/learning/home'),
  updateProfile: (payload: Partial<OnboardingPayload> & { metadata?: Record<string, unknown> }) =>
    api.put<{ success: boolean; profile: LearnerProfile }>('/api/learning/profile', payload),
  completeOnboarding: (payload: OnboardingPayload) =>
    api.post<{ success: boolean; profile: LearnerProfile; recommendations: SkillRecommendation[] }>('/api/learning/onboarding', payload),
  listRecommendations: () => api.get<{ success: boolean; recommendations: SkillRecommendation[] }>('/api/learning/recommendations'),
  generateRecommendations: () => api.post<{ success: boolean; recommendations: SkillRecommendation[] }>('/api/learning/recommendations/generate', {}),
  getStreak: () => api.get<{ success: boolean; streak: LearningStreak }>('/api/learning/streak'),
  listSkills: () => api.get<{ success: boolean; skills: LearnerSkillProgress[] }>('/api/learning/skills'),
  recordActivity: (payload: Partial<LearningActivity> & { activityType: string }) =>
    api.post<{ success: boolean; activity: { id: string } }>('/api/learning/activities', payload),
  listCoaching: () => api.get<{ success: boolean; coaching: CoachingSession[] }>('/api/learning/coaching'),
  listEvidence: () => api.get<{ success: boolean; evidence: LearningEvidence[] }>('/api/learning/evidence'),
  listGuardianShares: () => api.get<{ success: boolean; shares: GuardianShare[] }>('/api/learning/guardian-shares'),
  createGuardianShare: (payload: { guardianEmail: string; relationshipLabel?: string | null; metadata?: Record<string, unknown> }) =>
    api.post<{ success: boolean; share: GuardianShare }>('/api/learning/guardian-shares', payload),
  resendGuardianShare: (shareId: string) =>
    api.post<{ success: boolean; share: GuardianShare }>(`/api/learning/guardian-shares/${shareId}/resend`, {}),
  revokeGuardianShare: (shareId: string) => api.delete<{ success: boolean; share: GuardianShare }>(`/api/learning/guardian-shares/${shareId}`),
  listGuardianLearners: () => api.get<{ success: boolean; learners: GuardianLearner[] }>('/api/learning/guardian/learners'),
  acceptGuardianShare: (learnerUserId: string) =>
    api.post<{ success: boolean; share: GuardianLearner }>(`/api/learning/guardian/learners/${learnerUserId}/accept`, {}),
  getGuardianProgressSummary: (learnerUserId: string) =>
    api.get<GuardianProgressSummaryResponse>(`/api/learning/guardian/learners/${learnerUserId}/progress-summary`),
  getCoach: () => api.get<{ success: boolean; coach: LearnerCoachProfile }>('/api/learning/coach/me'),
  optInAsCoach: (payload: { roleMode?: 'coach' | 'both'; headline?: string | null; bio?: string | null; coachingSkills?: string[] }) =>
    api.post<{ success: boolean; coach: LearnerCoachProfile }>('/api/learning/coach/opt-in', payload),
  frenchReadingComprehension: (payload: FrenchActionPayload) =>
    api.post<FrenchActionResult>('/api/learning/ai/french/reading-comprehension', payload),
  frenchWritingCorrection: (payload: FrenchActionPayload) =>
    api.post<FrenchActionResult>('/api/learning/ai/french/writing-correction', payload),
  frenchDictee: (payload: FrenchActionPayload) =>
    api.post<FrenchActionResult>('/api/learning/ai/french/dictee', payload),
  frenchProgressSummary: (payload: FrenchActionPayload) =>
    api.post<FrenchActionResult>('/api/learning/ai/french/progress-summary', payload),
  // Reusable content assignments — a learner only ever sees their own assignments.
  listAssignments: () =>
    api.get<{ success: boolean; assignments: TemplateAssignment[] }>('/api/learning/assignments'),
  getContentTemplate: (templateId: string) =>
    api.get<{ success: boolean; template: LearningTemplate }>(`/api/learning/content/templates/${templateId}`),
  submitContentAttempt: (payload: AttemptSubmission) =>
    api.post<{ success: boolean; attempt: LearnerAttempt }>('/api/learning/content/attempts', payload),
  submitContentFeedback: (payload: ContentFeedbackSubmission) =>
    api.post<{ success: boolean; feedback: Record<string, unknown> }>('/api/learning/content/feedback', payload),
}
